// frontend/src/components/Automations/schedulePicker.js
// Human schedule shapes → the exact { trigger_type, trigger_expr } pair the
// backend already accepts (createAutomation.validateCreateInput). Pure, no deps.
//
// Why this exists (2026-09-10, Automations tab Part A): the New-automation form
// exposed a trigger-type select + a raw cron box, and an empty box surfaced the
// backend's "trigger_expr did not yield a valid next run time" verbatim. The
// operator never types a cron string from this surface again — every shape
// below computes the pair itself, and anything invalid is null (Create stays
// disabled). The backend validator remains the backstop, untouched.

export const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const PICKER_SHAPES = [
  { id: 'daily',       label: 'Every day at' },
  { id: 'weekly',      label: 'Every week on' },
  { id: 'every_hours', label: 'Every N hours' },
  { id: 'once',        label: 'Once at' },
];

export const DEFAULT_PICKER = { shape: 'daily', time: '09:00', dow: '1', hours: '6', at: '' };

const MAX_HOURS = 168;

function parseClock(time) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(time ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]), min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return { h, min };
}

export function toTrigger(picker) {
  if (!picker || typeof picker !== 'object') return null;
  switch (picker.shape) {
    case 'daily': {
      const c = parseClock(picker.time);
      return c ? { trigger_type: 'cron', trigger_expr: `${c.min} ${c.h} * * *` } : null;
    }
    case 'weekly': {
      const c = parseClock(picker.time);
      const dow = String(picker.dow ?? '').trim();
      if (!c || !/^[0-6]$/.test(dow)) return null;
      return { trigger_type: 'cron', trigger_expr: `${c.min} ${c.h} * * ${dow}` };
    }
    case 'every_hours': {
      const n = Number(picker.hours);
      if (!Number.isInteger(n) || n < 1 || n > MAX_HOURS) return null;
      return { trigger_type: 'interval', trigger_expr: String(n * 3600) };
    }
    case 'once': {
      const raw = String(picker.at ?? '').trim();
      if (!raw) return null;
      const d = new Date(raw);
      if (Number.isNaN(d.getTime()) || d.getTime() <= Date.now()) return null;
      return { trigger_type: 'once', trigger_expr: d.toISOString() };
    }
    default:
      return null;
  }
}

/** The browser's IANA zone, or undefined so the backend default applies. */
export function browserTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; }
  catch { return undefined; }
}
